import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ProductCard } from "@/components/ProductCard";
import { ProductFilters, type ProductFilterValues } from "@/components/ProductFilters";
import { listProducts, getCategory } from "@/lib/catalog.functions";
import { categoryImage } from "@/data/categoryImages";
import { topCategories } from "@/data/topCategories";
import { categoryIntros, categoryContent } from "@/data/categoryIntros";

const searchSchema = z.object({
  q: z.string().optional(),
  min: z.coerce.number().min(0).max(1000000).optional(),
  max: z.coerce.number().min(0).max(1000000).optional(),
  sort: z.enum(["new", "low", "high", "popular"]).optional(),
});

export const Route = createFileRoute("/kategorije/$slug")({
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    type CategoryLoaderData = {
      meta: { name: string; description: string | null } | null;
      parent: { name: string; slug: string } | null;
      items: Array<{ slug: string; title: string; price: number | null; image: string | null }>;
      total: number;
    };
    let result: CategoryLoaderData | null = null;
    try {
      const [res, prod] = await Promise.all([
        getCategory({ data: { slug: params.slug } }),
        listProducts({ data: { categorySlug: params.slug, limit: 12, sort: "popular" } }).catch(() => null),
      ]);
      const c = res?.category as {
        name?: string;
        description?: string | null;
        parent?: { name: string; slug: string } | Array<{ name: string; slug: string }> | null;
      } | null;
      const items = ((prod?.products ?? []) as Array<{ slug: string; title: string; price: number | null; sale_price: number | null; featured_image_url: string | null }>)
        .filter((p) => p.slug && p.title)
        .map((p) => ({
          slug: p.slug,
          title: p.title,
          price: p.sale_price ?? p.price ?? null,
          image: p.featured_image_url ?? null,
        }));
      const known =
        Boolean(c) ||
        items.length > 0 ||
        Boolean(categoryIntros[params.slug]) ||
        Boolean(categoryContent[params.slug]) ||
        topCategories.some((t) => t.slug === params.slug || t.subcategories.some((s) => s.slug === params.slug));
      if (known) {
        const rawParent = c?.parent;
        const p = Array.isArray(rawParent) ? rawParent[0] : rawParent;
        result = c
          ? {
              meta: { name: c.name ?? params.slug, description: c.description ?? null },
              parent: p ? { name: p.name, slug: p.slug } : null,
              items,
              total: prod?.total ?? items.length,
            }
          : { meta: null, parent: null, items, total: prod?.total ?? 0 };
      }
    } catch {
      // Tehnična napaka zaledja ne sme povzročiti 404.
      return { meta: null, parent: null, items: [], total: 0 } as CategoryLoaderData;
    }
    if (!result) throw notFound();
    return result;
  },




  head: ({ params, loaderData }) => {
    const m = loaderData?.meta;
    const url = `https://kupish.si/kategorije/${params.slug}`;
    const name = m?.name ?? params.slug;
    const title = `${CATEGORY_TITLE[params.slug] ?? name} — kupish.si`;
    const keywordDesc = CATEGORY_META[params.slug];
    const baseDesc = (m?.description ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const desc = (keywordDesc ?? baseDesc).slice(0, 160)
      || `${name} na kupish.si — novi izdelki preverjenih ponudnikov na enem mestu. Primerjajte ponudbo več trgovin in naročite v enem koraku.`;
    const img = categoryImage(params.slug);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "robots", content: m ? "index, follow, max-image-preview:large, max-snippet:-1" : "noindex, follow" },

        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "sl_SI" },
        { property: "og:site_name", content: "kupish.si" },
        { name: "twitter:card", content: img ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(img ? [
          { property: "og:image", content: img.startsWith("http") ? img : `https://kupish.si${img}` },
          { property: "og:image:alt", content: title },
          { name: "twitter:image", content: img.startsWith("http") ? img : `https://kupish.si${img}` },
          { name: "twitter:image:alt", content: title },
        ] : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...(img ? [{ rel: "preload", as: "image", href: img, fetchpriority: "high" }] : []),
      ],
      scripts: (() => {
        const abs = (u: string) => (u.startsWith("http") ? u : `https://kupish.si${u}`);
        const parent = loaderData?.parent ?? null;
        const items = loaderData?.items ?? [];
        const itemList = items.length
          ? {
              "@type": "ItemList",
              name: `Izdelki v kategoriji ${name}`,
              numberOfItems: loaderData?.total ?? items.length,
              itemListElement: items.map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "Product",
                  name: p.title,
                  url: `https://kupish.si/izdelki/${p.slug}`,
                  ...(p.image ? { image: abs(p.image) } : {}),
                  ...(p.price != null
                    ? {
                        offers: {
                          "@type": "Offer",
                          price: Number(p.price).toFixed(2),
                          priceCurrency: "EUR",
                          availability: "https://schema.org/InStock",
                          url: `https://kupish.si/izdelki/${p.slug}`,
                        },
                      }
                    : {}),
                },
              })),
            }
          : null;
        return [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name,
              description: desc,
              url,
              inLanguage: "sl-SI",
              isPartOf: { "@type": "WebSite", name: "kupish.si", url: "https://kupish.si/" },
              ...(img ? { primaryImageOfPage: { "@type": "ImageObject", url: abs(img) } } : {}),
              ...(itemList ? { mainEntity: itemList } : {}),
            }),
          },
          ...(categoryContent[params.slug]
            ? [{
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: categoryContent[params.slug]!.faq.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              }]
            : []),
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Domov", item: "https://kupish.si/" },
                { "@type": "ListItem", position: 2, name: "Kategorije", item: "https://kupish.si/kategorije" },
                ...(parent
                  ? [
                      { "@type": "ListItem", position: 3, name: parent.name, item: `https://kupish.si/kategorije/${parent.slug}` },
                      { "@type": "ListItem", position: 4, name, item: url },
                    ]
                  : [{ "@type": "ListItem", position: 3, name, item: url }]),
              ],
            }),
          },
        ];
      })(),

    };
  },
  notFoundComponent: () => (
    <section className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Kategorija ne obstaja</h1>
      <p className="mt-4 text-muted-foreground">Ta kategorija ni na voljo ali je bila odstranjena.</p>
      <Link to="/kategorije" className="mt-6 inline-block text-accent">Poglejte vse kategorije</Link>
    </section>
  ),
  component: Page,

});

// Keyword-rich meta descriptions for top categories (low-competition Slovenian long-tail keywords)
// Keyword-rich titles for categories with the highest impression volume in Search Console.
const CATEGORY_TITLE: Record<string, string> = {
  "drogerija": "Drogerija — čistila, krpe in izdelki za nego doma",
  "nega-doma": "Nega doma — čistila in pripomočki za čiščenje",
  "cistila": "Čistila za dom — kuhinja, kopalnica, steklo in tla",
  "krpe-za-ciscenje": "Krpe za čiščenje — mikrovlaknate in univerzalne",
  "osebna-nega": "Osebna nega — izdelki za vsakodnevno nego",
  "elektronika-avdio": "Elektronika in avdio — slušalke, zvočniki, kamere",
  "slusalke": "Slušalke — brezžične, ušesne in naglavne",
  "prenosni-zvocniki": "Prenosni Bluetooth zvočniki za dom in pot",
  "avdio-video": "Avdio in video oprema — kamere in varuške",
  "nadzorne-kamere": "Nadzorne kamere za dom in poslovni prostor",
  "otroske-varuske": "Otroške varuške — avdio in video modeli",
  "avto-elektronika": "Avto elektronika — kamere, prostoročni sistemi",
  "avto-kamere": "Avto kamere — snemanje vožnje v Full HD",
  "prostorocno-telefoniranje": "Prostoročno telefoniranje v avtu",
  "fm-radiji": "FM oddajniki za avto — glasba s telefona",
  "avtomobilska-oprema": "Avtomobilska oprema — polnilci, nosilci, držala",
  "napajlniki-in-polnilci": "Avtomobilski polnilci in napajalniki",
  "nosilci-in-drzala": "Nosilci in držala za telefon v avtu",
  "dom-vrt": "Dom in vrt — kuhinja, tekstil in oprema za vrt",
  "kuhinja": "Kuhinjski pripomočki in oprema za kuhinjo",
  "prti": "Namizni prti in tekstil za jedilno mizo",
  "vrt": "Vrt in terasa — orodje in oprema",
  "stojala": "Stojala in nosilci za dom",
  "moda-dodatki": "Moda in dodatki — nakit in modni dodatki",
  "nakit": "Nakit — uhani, verižice, zapestnice in prstani",
  "sport-prosti-cas": "Šport in prosti čas — oprema za aktivnosti",
  "sportne-kamere": "Športne kamere za snemanje aktivnosti",
};

const CATEGORY_META: Record<string, string> = {
  "drogerija": "Čistila, krpe, pripomočki za čiščenje in izdelki za osebno nego preverjenih prodajalcev na kupish.si. Primerjajte namen uporabe, volumen in ceno.",
  "nega-doma": "Vse za redno čiščenje doma: univerzalna in specialna čistila, mikrovlaknate krpe, ščetke in pripomočki, ki skrajšajo čas čiščenja.",
  "cistila": "Čistila za kuhinjo, kopalnico, steklo in tla. Pri vsakem izdelku preverite namen uporabe, volumen in navodila proizvajalca.",
  "krpe-za-ciscenje": "Mikrovlaknate in univerzalne krpe za suho ter mokro čiščenje. Trpežne krpe zmanjšajo porabo čistil in zdržijo več sto pranj.",
  "pripomocki-za-ciscenje": "Ščetke, brisalci in nastavki za čiščenje težje dostopnih površin v gospodinjstvu.",
  "antibakterijski-izdelki": "Izdelki za razkuževanje površin in rok. Pred uporabo preverite sestavo in navodila proizvajalca.",
  "osebna-nega": "Izdelki za vsakodnevno osebno nego iz ponudbe trgovin na kupish.si — nega kože, las in praktični pripomočki.",
  "elektronika-avdio": "Slušalke, prenosni zvočniki, kamere in zabavna elektronika. Pri vsakem izdelku so navedene ključne specifikacije in prodajalec.",
  "slusalke": "Brezžične in žične slušalke za glasbo, klice in šport. Primerjajte tip, različico Bluetootha, avtonomijo in odpornost proti znoju.",
  "prenosni-zvocniki": "Prenosni Bluetooth zvočniki za dom, teraso in izlet. Pomembni podatki so moč, avtonomija baterije in odpornost proti vodi.",
  "telefonija": "Dodatki za mobilne telefone — polnilci, kabli in držala za vsakodnevno uporabo.",
  "avdio-video": "Naprave za sliko in zvok — nadzorne in športne kamere, otroške varuške ter avdio oprema za dom.",
  "zabavna-elektronika": "Slušalke, prenosni zvočniki in dodatki za telefonijo iz ponudbe trgovin na kupish.si.",
  "nadzorne-kamere": "Kamere za nadzor doma in poslovnega prostora. Primerjajte ločljivost, nočno snemanje in način shranjevanja posnetkov.",
  "otroske-varuske": "Avdio in video otroške varuške. Pred nakupom preverite doseg signala, kakovost prenosa in trajanje baterije.",
  "avto-elektronika": "Avtomobilske kamere, prostoročni sistemi in FM oddajniki, ki povečajo varnost in udobje med vožnjo.",
  "avto-kamere": "Avto kamere snemajo vožnjo v zanki in ob trku zaklenejo posnetek. Primerjajte ločljivost, vidni kot in nočno snemanje.",
  "prostorocno-telefoniranje": "Prostoročni sistemi za telefoniranje med vožnjo brez držanja telefona v roki, kot zahteva zakonodaja.",
  "fm-radiji": "FM oddajniki predvajajo glasbo s telefona prek avtoradia — rešitev za vozila brez povezave Bluetooth.",
  "avtomobilska-oprema": "Polnilci, napajalniki, nosilci in držala za telefon ali navigacijo. Izberite glede na način pritrditve in moč polnjenja.",
  "napajlniki-in-polnilci": "Avtomobilski polnilci in napajalniki za telefone in kamere. Preverite izhodno moč in tip priključka.",
  "nosilci-in-drzala": "Nosilci in držala za telefon, navigacijo ali kamero — za prezračevalno režo, steklo ali armaturno ploščo.",
  "dom-vrt": "Kuhinjski pripomočki, stojala, namizni tekstil in oprema za vrt iz ponudbe trgovin na kupish.si.",
  "kuhinja": "Pripomočki in oprema za kuhinjo — posoda, noži in praktična pomagala za pripravo hrane.",
  "prti": "Namizni prti in tekstil za jedilno mizo. Pri izbiri upoštevajte dimenzije mize, material in vzdrževanje.",
  "vrt": "Izdelki za vrt, teraso in balkon — orodje, oprema in pripomočki za urejanje zelenih površin.",
  "stojala": "Stojala in nosilci za dom — praktične rešitve za red in shranjevanje.",
  "moda-dodatki": "Modni dodatki in nakit iz ponudbe trgovin na kupish.si. Pri vsakem izdelku sta navedena material in prodajalec.",
  "nakit": "Uhani, verižice, zapestnice in prstani iz ponudbe trgovin na kupish.si. Pred nakupom preverite material in dimenzije.",
  "sport-prosti-cas": "Oprema za šport in prosti čas — športne kamere in pripomočki za aktivnosti na prostem.",
  "sportne-kamere": "Športne kamere za kolesarjenje, smučanje in druge aktivnosti. Preverite ločljivost, stabilizacijo in vodoodpornost.",
};



function Page() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const loaderData = Route.useLoaderData();
  const navigate = useNavigate({ from: "/kategorije/$slug" });
  const fetchCat = useServerFn(getCategory);
  const fetchProducts = useServerFn(listProducts);

  const { data: catData } = useQuery({ queryKey: ["cat", slug], queryFn: () => fetchCat({ data: { slug } }) });
  const { data, isLoading } = useQuery({
    queryKey: ["cat-products", slug, search],
    queryFn: () => fetchProducts({ data: {
      categorySlug: slug,
      q: search.q,
      priceMin: search.min,
      priceMax: search.max,
      sort: search.sort ?? "popular",
      limit: 120,
    } }),
  });

  const cat = catData?.category;
  const children = catData?.children ?? [];
  const list = data?.products ?? [];

  if (!isLoading && !cat) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="font-serif text-4xl">Kategorija ne obstaja</h1>
        <Link to="/kategorije" className="mt-6 inline-block underline underline-offset-4">Vse kategorije</Link>
      </div>
    );
  }

  const rawParent = (cat as unknown as { parent?: { id: string; name: string; slug: string } | Array<{ id: string; name: string; slug: string }> | null })?.parent;
  // Prefer SSR loader data so the breadcrumb renders in the first paint (no CLS).
  const parent = (Array.isArray(rawParent) ? rawParent[0] : rawParent) ?? loaderData.parent ?? null;
  const heroImg = categoryImage(cat?.slug ?? slug, parent?.slug);
  const top = topCategories.find((t) => t.slug === cat?.slug || t.slug === slug);

  const displayName = cat?.name ?? loaderData.meta?.name ?? top?.name ?? slug.replace(/-/g, " ");
  const displayDescription = cat?.description || top?.description || loaderData.meta?.description;

  const update = (patch: Partial<ProductFilterValues>) => {
    navigate({ search: (prev: Record<string, unknown>) => {
      const next: Record<string, unknown> = { ...prev, ...patch };
      Object.keys(next).forEach((k) => {
        if (next[k] == null || next[k] === "") delete next[k];
      });
      return next;
    } });
  };

  const heroSrc = top?.image_url ?? heroImg;
  const subs = top
    ? top.subcategories.map((s) => ({ id: s.slug, name: s.name, slug: s.slug, Icon: s.Icon }))
    : children.map((s) => ({ id: s.id, name: s.name, slug: s.slug, Icon: null as null }));

  return (
    <>
      <div className="relative overflow-hidden bg-card min-h-[250px] md:min-h-[270px]">

        {heroSrc && (
          <img
            src={heroSrc}
            alt=""
            aria-hidden
            width={1600}
            height={540}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover opacity-90 md:scale-[1.12]"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 via-white/15 to-white/50" />

        <div className="relative z-10 px-4 pt-9 max-w-[88%] sm:px-6 md:max-w-[70%] md:px-10 md:pt-14">
          <h1 className="font-serif text-3xl leading-tight text-black sm:text-4xl md:text-5xl lg:text-6xl">
            {displayName}
          </h1>
          {displayDescription && (
            <p className="mt-3 max-w-[34ch] text-sm leading-snug text-black/85 line-clamp-3 md:mt-6 md:max-w-[40ch] md:text-base">
              {displayDescription}
            </p>
          )}
        </div>

        {subs.length > 0 && (
          <div className="absolute inset-x-0 bottom-0 z-10 p-3 md:p-5">
            <div className="flex flex-wrap items-center gap-2">
              {subs.map((sub) => (
                <Link
                  key={sub.id}
                  to="/kategorije/$slug"
                  params={{ slug: sub.slug }}
                  className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-accent/40 bg-background/90 backdrop-blur-sm px-3 py-2 text-xs text-foreground hover:border-accent hover:text-accent transition-colors sm:min-h-0 md:py-1.5"
                >
                  {sub.Icon && <sub.Icon className="h-3.5 w-3.5" strokeWidth={1.5} />}
                  {sub.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {parent && (
          <div className="mb-4 text-sm">
            <Link to="/kategorije/$slug" params={{ slug: parent.slug }} className="text-muted-foreground hover:text-accent transition-colors">
              ← {parent.name}
            </Link>
          </div>
        )}
        {(() => {
          // Keyed by the route slug (not the client-fetched category) so the
          // intro is part of the first paint and does not shift the layout.
          const intro = categoryIntros[cat?.slug ?? slug];
          if (!intro) return null;
          return (
            <div className="mb-6 max-w-3xl">
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {intro.lead}
              </p>
              {intro.links && intro.links.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Preberite:</span>
                  {intro.links.map((l) => (
                    <Link key={l.to} to={l.to} className="text-accent hover:underline">
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        <ProductFilters
          values={{ q: search.q, min: search.min, max: search.max, sort: search.sort }}
          onChange={update}
          onReset={() => navigate({ search: {} })}
        />


        {isLoading ? (
          <div className="mt-16 text-center text-muted-foreground py-16">Nalagam…</div>
        ) : list.length === 0 ? (
          <div className="mt-16 text-center text-muted-foreground py-16 border border-dashed border-border">
            Ni zadetkov za izbrane filtre.
          </div>
        ) : (
          <>
            <h2 className="sr-only">Seznam izdelkov</h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4 mt-10">
              {list.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </>
        )}

        {(() => {
          const extra = categoryContent[slug];
          if (!extra) return null;
          return (
            <div className="mt-16 border-t border-border pt-10 max-w-3xl">
              {extra.sections.map((sec) => (
                <div key={sec.h} className="mb-8">
                  <h2 className="font-serif text-xl md:text-2xl">{sec.h}</h2>
                  {sec.p.map((par, i) => (
                    <p key={i} className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                      {par}
                    </p>
                  ))}
                </div>
              ))}
              <h2 className="font-serif text-xl md:text-2xl mt-10">Pogosta vprašanja</h2>
              <dl className="mt-4 space-y-5">
                {extra.faq.map((f) => (
                  <div key={f.q}>
                    <dt className="text-sm md:text-base font-medium text-foreground">{f.q}</dt>
                    <dd className="mt-1 text-sm md:text-base leading-relaxed text-muted-foreground">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })()}
      </section>
    </>
  );
}
